/**
 * Pure helpers behind the scenario editor: the outline's node order, reference counting and renaming
 * for the ids other parts of a scenario point at, new-node defaults, and the mapping from an issue's
 * path to the part of the editor that fixes it. Kept free of Preact so the rules are easy to read in
 * one place and cheap to run on every keystroke.
 */
import {
  type NodeType,
  nodeSuccessors,
  type Scenario,
  type ScenarioNode,
  type ValidationIssue,
} from "@shared/scenario";

export type SectionId = "overview" | "roles" | "scoring" | "injects" | "badges";
export type Selection = { kind: "section"; id: SectionId } | { kind: "node"; id: string };
export const SECTIONS: SectionId[] = ["overview", "roles", "scoring", "injects", "badges"];
export const NODE_TYPES: NodeType[] = [
  "narrative",
  "call",
  "decision",
  "poll",
  "reveal",
  "branch",
  "debrief",
];

/** Node ids, option ids, role ids and dimension ids are kebab-case; variables are identifiers. */
export const KEBAB = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export const IDENT = /^[A-Za-z][A-Za-z0-9_]*$/;

/**
 * Breadth-first from `start`, the order the console's map uses, then the inject entry points and
 * whatever only they reach; nodes neither reaches are returned apart as unreachable.
 */
export function outlineOrder(s: Scenario): { reachable: string[]; unreachable: string[] } {
  const seen = new Set<string>();
  const reachable: string[] = [];
  const walk = (from: string) => {
    const queue = [from];
    while (queue.length) {
      const id = queue.shift()!;
      if (seen.has(id) || !s.nodes[id]) continue;
      seen.add(id);
      reachable.push(id);
      for (const next of nodeSuccessors(s.nodes[id]!)) if (!seen.has(next)) queue.push(next);
    }
  };
  walk(s.start);
  for (const inj of s.injects) walk(inj.node);
  const unreachable = Object.keys(s.nodes).filter((id) => !seen.has(id));
  return { reachable, unreachable };
}

export type RefKind = "node" | "role" | "dimension" | "variable";

/**
 * Visit every place a scenario names an id of `kind`. The visitor returns the replacement (or the
 * same value) so one walk serves both counting and renaming, and the two can never disagree.
 */
function visitRefs(s: Scenario, kind: RefKind, visit: (id: string) => string): void {
  if (kind === "node") {
    s.start = visit(s.start);
    for (const node of Object.values(s.nodes)) {
      switch (node.type) {
        case "narrative":
        case "call":
        case "poll":
        case "reveal":
          if (node.next !== null) node.next = visit(node.next);
          break;
        case "decision":
          for (const o of node.options) o.next = visit(o.next);
          break;
        case "branch":
          for (const c of node.conditions) c.next = visit(c.next);
          node.else = visit(node.else);
          break;
        case "debrief":
          break;
      }
    }
    for (const inj of s.injects) {
      inj.node = visit(inj.node);
      inj.availableAfter = inj.availableAfter.map(visit);
    }
    return;
  }
  if (kind === "role") {
    for (const node of Object.values(s.nodes)) {
      node.roleNotes = renameKeys(node.roleNotes, visit);
      if (node.type === "call" && node.callee.roleId) node.callee.roleId = visit(node.callee.roleId);
      if (node.type === "decision") {
        if (node.voters !== "all") node.voters = node.voters.map(visit);
        for (const o of node.options) o.roleBonus = o.roleBonus.map(visit);
      }
    }
    return;
  }
  if (kind === "dimension") {
    for (const node of Object.values(s.nodes)) {
      if (node.type !== "decision") continue;
      for (const o of node.options) o.effects.score = renameKeys(o.effects.score, visit);
    }
    for (const b of s.badges) {
      if (b.condition.kind === "teamDimension") b.condition.dimension = visit(b.condition.dimension);
    }
    return;
  }
  for (const node of Object.values(s.nodes)) {
    if (node.type === "decision") {
      for (const o of node.options) o.effects.vars = renameKeys(o.effects.vars, visit);
    }
    if (node.type === "branch") for (const c of node.conditions) c.var = visit(c.var);
  }
  for (const b of s.badges) {
    if (b.condition.kind === "teamVarBelow") b.condition.var = visit(b.condition.var);
  }
}

function renameKeys<T>(obj: Record<string, T>, visit: (id: string) => string): Record<string, T> {
  const out: Record<string, T> = {};
  for (const [k, v] of Object.entries(obj)) out[visit(k)] = v;
  return out;
}

/** How many places point at `id` (not counting its own definition). */
export function countRefs(s: Scenario, kind: RefKind, id: string): number {
  let n = 0;
  visitRefs(structuredClone(s), kind, (x) => {
    if (x === id) n++;
    return x;
  });
  return n;
}

/** Rename an id and every reference to it; returns a new scenario. */
export function renameId(s: Scenario, kind: RefKind, from: string, to: string): Scenario {
  const next = structuredClone(s);
  visitRefs(next, kind, (x) => (x === from ? to : x));
  if (kind === "node") {
    // Rebuild the record so the renamed node keeps its place in the author's order.
    const nodes: Record<string, ScenarioNode> = {};
    for (const [k, v] of Object.entries(next.nodes)) nodes[k === from ? to : k] = v;
    next.nodes = nodes;
  } else if (kind === "role") {
    for (const r of next.roles) if (r.id === from) r.id = to;
  } else if (kind === "dimension") {
    for (const d of next.dimensions) if (d.id === from) d.id = to;
  } else {
    next.variables = renameKeys(next.variables, (k) => (k === from ? to : k));
  }
  return next;
}

/** The ids already taken for a kind, so a rename can refuse a collision. */
export function idsOf(s: Scenario, kind: RefKind): string[] {
  if (kind === "node") return Object.keys(s.nodes);
  if (kind === "role") return s.roles.map((r) => r.id);
  if (kind === "dimension") return s.dimensions.map((d) => d.id);
  return Object.keys(s.variables);
}

/** The first `prefix-N` not in `taken`. */
export function freshId(prefix: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  for (let n = 1; ; n++) {
    const id = `${prefix}-${n}`;
    if (!used.has(id)) return id;
  }
}

/*
 * Default content for new nodes. A scenario's source text is English whatever language the editor's
 * interface is in (docs/scenario-authoring.md: translations are derived from it), so these are
 * placeholder scenario content rather than interface strings, and they deliberately do not go
 * through the locale files.
 */
export function newNode(type: NodeType, s: Scenario): ScenarioNode {
  const debrief = Object.keys(s.nodes).find((id) => s.nodes[id]?.type === "debrief") ?? s.start;
  const firstVar = Object.keys(s.variables)[0] ?? "";
  const base = { roleNotes: {} as Record<string, string> };
  switch (type) {
    case "narrative":
      return { ...base, type, title: "New scene", body: "What the room sees and hears.", next: null };
    case "call":
      return {
        ...base,
        type,
        title: "New call",
        caller: { name: "Caller", displayNumber: "+1 555 0100", spoofed: false },
        callee: { name: "Callee" },
        transcript: [{ speaker: "caller", text: "Hello?" }],
        redFlags: [],
        next: null,
      };
    case "decision":
      return {
        ...base,
        type,
        title: "New decision",
        prompt: "What do you do?",
        timerSec: 90,
        voters: "all",
        options: [newOption("option-1", "best", debrief), newOption("option-2", "bad", debrief)],
      };
    case "poll":
      return {
        ...base,
        type,
        title: "New poll",
        prompt: "What do you think?",
        timerSec: 60,
        options: [
          { id: "yes", label: "Yes" },
          { id: "no", label: "No" },
        ],
        next: null,
      };
    case "reveal":
      return {
        ...base,
        type,
        title: "What really happened",
        body: "The explanation.",
        lessons: [],
        next: null,
      };
    case "branch":
      return {
        ...base,
        type,
        title: "New branch",
        conditions: [{ var: firstVar, op: ">=", value: 1, next: debrief }],
        else: debrief,
      };
    case "debrief":
      return { ...base, type, title: "Debrief", body: "What the room takes away." };
  }
}

export function newOption(id: string, quality: "best" | "good" | "risky" | "bad", next: string) {
  return {
    id,
    label: quality === "best" ? "The careful choice" : "The rushed choice",
    quality,
    feedback: "Explain at the reveal why this choice helps or hurts.",
    effects: { vars: {}, score: {} },
    next,
    roleBonus: [] as string[],
    tags: [] as string[],
  };
}

/** Where an issue's path points in the editor. */
export function selectionForPath(s: Scenario, path: string): Selection {
  const parts = path.split(".");
  const head = parts[0] ?? "";
  if (head === "nodes") {
    const id = parts[1];
    if (id && s.nodes[id]) return { kind: "node", id };
    return s.nodes[s.start] ? { kind: "node", id: s.start } : { kind: "section", id: "overview" };
  }
  if (head === "roles") return { kind: "section", id: "roles" };
  if (head === "dimensions" || head === "variables") return { kind: "section", id: "scoring" };
  if (head === "injects") return { kind: "section", id: "injects" };
  if (head === "badges") return { kind: "section", id: "badges" };
  return { kind: "section", id: "overview" };
}

/** Issues that belong to one node or one section, for the outline's counts. */
export function issuesFor(s: Scenario, sel: Selection, issues: ValidationIssue[]): ValidationIssue[] {
  return issues.filter((i) => {
    const target = selectionForPath(s, i.path);
    if (sel.kind === "node") {
      // "nodes" on its own (no debrief, too few decisions) is about the whole graph, not the start node.
      const parts = i.path.split(".");
      return parts[0] === "nodes" && parts[1] === sel.id && target.kind === "node";
    }
    return target.kind === "section" && target.id === sel.id;
  });
}

export function sameSelection(a: Selection, b: Selection): boolean {
  return a.kind === b.kind && a.id === b.id;
}

/** The highest score each dimension can earn at one decision: the per-decision `possible` in docs. */
export function bestPossible(node: Extract<ScenarioNode, { type: "decision" }>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const o of node.options) {
    for (const [dim, v] of Object.entries(o.effects.score)) out[dim] = Math.max(out[dim] ?? 0, v);
  }
  return out;
}

/** Move an array item by `delta`, returning a new array. */
export function move<T>(list: T[], index: number, delta: number): T[] {
  const to = index + delta;
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(index, 1);
  next.splice(to, 0, item as T);
  return next;
}
